// components/DarkModeContext.js dosyası
import React, { useEffect, useState, useCallback } from 'react';

// Karanlık mod context
const DarkModeContext = React.createContext({
	darkMode: false,
	toggleDarkMode: () => {},
});

export const DarkModeProvider = ({ children }) => {
	// localStorage'dan karanlık mod durumunu al
	const [darkMode, setDarkMode] = useState(() => {
		const savedMode = localStorage.getItem('darkMode');
		if (savedMode !== null) {
			return savedMode === 'true';
		}
		return false;
	});
    
    // Forceupdate için sayaç
    const [updateCounter, setUpdateCounter] = useState(0);
    
    // React bileşenlerini zorlamak için forceUpdate
    const forceUpdate = useCallback(() => {
        setUpdateCounter(prev => prev + 1);
    }, []);

	// CSS stilleri uygulanması
	useEffect(() => {
		if (darkMode) {
			// Karanlık mod açıksa, stil ekle
			const styleElement = document.getElementById('dark-mode-styles') || document.createElement('style');
			styleElement.id = 'dark-mode-styles';
			styleElement.textContent = `
        /* Ana menü ve arka plan */
        .navbar, .ui.vertical.icon.menu.navbar {
          background-color: #121212 !important;
          border-right: 1px solid #333333 !important;
        }
        
        /* Header öğeleri ve ikonlar */
        .header-item, .header-item.item, .item, .ui.menu .item {
          color: #e0e0e0 !important;
        }
        
        .header-item .icon, .header-item.item .icon, .ui.menu .item .icon {
          color: #e0e0e0 !important;
        }
        
        /* SVG ikonları */
        .header-item svg, .ui.menu .item svg, svg {
          fill: #e0e0e0 !important;
        }
        
        /* Menü ayırıcı */
        .header-seperator {
          background-color: #333333 !important;
        }
        
        /* Menü öğeleri */      
        .console-logo {
          background-color: #1b1c1d !important;
        }

        /* Logo */
        .console-logo img {
          filter: brightness(0.9) !important;
        }
        
        /* Aktif menü öğeleri */
        .ui.menu .active.item, .ui.menu .active.item:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
        }
        
        /* Menü öğesi hover efekti */
        .ui.menu .item:hover {
          background-color: rgba(255, 255, 255, 0.05) !important;
        }
        
        /* Font ikonları */
        .fa {
          color: #e0e0e0 !important;
        }
        
        /* Semantic UI bileşenleri */
        .ui.segment {
          background-color: #1a1a1a !important;
          color: #e0e0e0 !important;
          border-color: #333333 !important;
        }
      `;

			if (!document.getElementById('dark-mode-styles')) {
				document.head.appendChild(styleElement);
			}
		} else {
			// Karanlık mod kapalıysa stil etiketini kaldır
			const styleElement = document.getElementById('dark-mode-styles');
			if (styleElement) {
				styleElement.remove();
			}
		}
	}, [darkMode, updateCounter]); // updateCounter'ı bağımlılık olarak ekle

	// Chat iframe'inden gelen mesajları dinle
	useEffect(() => {
		const handleMessage = (event) => {
			if (event.data && event.data.type === 'DARK_MODE_CHANGED') {
				console.log('Chat uygulamasından karanlık mod mesajı alındı:', event.data.darkMode);
                setDarkMode(event.data.darkMode);
                localStorage.setItem('darkMode', event.data.darkMode);
                
                // React bileşenlerini zorla güncelle
                forceUpdate();
			}
		};

		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [forceUpdate]);

	// Karanlık modu değiştirme fonksiyonu
	const toggleDarkMode = useCallback(() => {
		const newDarkMode = !darkMode;
		console.log('Karanlık mod değiştiriliyor:', newDarkMode);
        
        // Durumu güncelle ve React'ı zorla render et
        setDarkMode(newDarkMode);
        localStorage.setItem('darkMode', newDarkMode);
        forceUpdate();

		// Chat iframe'ine bildir
		try {
			const chatFrames = document.querySelectorAll('iframe');
			chatFrames.forEach((frame) => {
				if (frame.contentWindow) {
					console.log('iframe\'e karanlık mod mesajı gönderiliyor');
					frame.contentWindow.postMessage(
						{
							type: 'TOGGLE_DARK_MODE',
						},
						'*'
					);
				}
			});
		} catch (e) {
			console.error('Chat iframe ile iletişim kurulamadı:', e);
		}
	}, [darkMode, forceUpdate]);

	// Tüm React bileşenlerini zorla güncellemek için
	useEffect(() => {
		// darkMode değiştiğinde tüm bileşenleri zorlamak için özel bir event ekleyelim
		const darkModeChangeHandler = () => {
			forceUpdate();
		};
		
		// Özel event listener ekle
		document.addEventListener('darkModeChanged', darkModeChangeHandler);
		
		// Sayfa yüklendiğinde bir kez başlangıç durumunu güncelle
		forceUpdate();
		
		return () => {
			document.removeEventListener('darkModeChanged', darkModeChangeHandler);
		};
	}, [forceUpdate]);

	// Ek bir event dispatch mekanizması
	useEffect(() => {
		// darkMode değiştiğinde özel event tetikle
		document.dispatchEvent(new CustomEvent('darkModeChanged'));
	}, [darkMode]);

	return (
		<DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
			{React.Children.map(children, child => {
                // React.cloneElement kullanarak her bir child'a key prop'u ekle
                // Bu, React'ın child'ların değiştiğini fark etmesini sağlar
                if (React.isValidElement(child)) {
                    return React.cloneElement(child, {
                        key: `dark-mode-${updateCounter}-${darkMode}`,
                    });
                }
                return child;
            })}
		</DarkModeContext.Provider>
	);
};

// Karanlık modu kullanmak için hook
export const useDarkMode = () => {
	return React.useContext(DarkModeContext);
};

export default DarkModeContext;